// New design system component: declarative filter strip replacing per-page inline filter grids

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// i18n
import { useTranslation } from "react-i18next";

const smallInputSx = { "& .MuiOutlinedInput-root": { height: "38px" } };
const smallFont = { style: { fontSize: "0.8rem" } };

function FilterBar({ fields, values, onChange, onReset, children, actions }) {
  const { t } = useTranslation();

  const renderField = (field) => {
    const { name, label, type, options, props: fieldProps } = field;
    const value = values[name] !== undefined && values[name] !== null ? values[name] : "";

    if (type === "select") {
      return (
        <TextField
          select
          label={label}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          fullWidth
          size="small"
          variant="outlined"
          InputProps={smallFont}
          InputLabelProps={smallFont}
          sx={{ ...smallInputSx, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
          {...fieldProps}
        >
          <MenuItem value="" style={{ fontSize: "0.8rem" }}>
            {t("all")}
          </MenuItem>
          {(options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value} style={{ fontSize: "0.8rem" }}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (type === "date") {
      return (
        <TextField
          label={label}
          type="date"
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          fullWidth
          size="small"
          variant="outlined"
          InputProps={smallFont}
          InputLabelProps={{ shrink: true, ...smallFont }}
          sx={smallInputSx}
          {...fieldProps}
        />
      );
    }

    // default: free text
    return (
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        fullWidth
        size="small"
        variant="outlined"
        InputProps={smallFont}
        InputLabelProps={smallFont}
        sx={smallInputSx}
        {...fieldProps}
      />
    );
  };

  return (
    <MDBox
      mb={2}
      px={2}
      py={1.5}
      sx={({ palette: { background }, borders: { borderWidth, borderColor, borderRadius } }) => ({
        border: `${borderWidth[1]} solid ${borderColor}`,
        borderRadius: borderRadius.lg,
        background: background.hover,
      })}
    >
      <Grid container spacing={1} alignItems="center">
        {fields.map((field) => (
          <Grid key={field.name} item xs={12} sm={6} md={3} lg={field.width || true}>
            {renderField(field)}
          </Grid>
        ))}
        {children}
        <Grid item xs={12} sm={6} md={3} lg="auto">
          <MDBox display="flex" alignItems="center" gap={1}>
            {onReset && (
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={onReset}
                size="small"
                style={{ whiteSpace: "nowrap" }}
              >
                {t("reset")}
              </MDButton>
            )}
            {actions}
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

FilterBar.defaultProps = {
  fields: [],
  values: {},
  onChange: () => {},
  onReset: null,
  children: null,
  actions: null,
};

FilterBar.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.node,
      type: PropTypes.oneOf(["text", "select", "date"]),
      options: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.any, label: PropTypes.node })
      ),
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      props: PropTypes.object,
    })
  ),
  values: PropTypes.object,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  children: PropTypes.node,
  actions: PropTypes.node,
};

export default FilterBar;
